import { useEffect } from "react";

export const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "").replace(/\/$/, "");

function setMeta(attr, key, content) {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
    el.setAttribute("content", content);
}

export function useSeo({ title, description, path = "/", jsonLd, noindex = false }) {
    useEffect(() => {
        const full = title ? `${title} | Sift` : "Sift — Where has my money gone?";
        document.title = full;
        if (description) { setMeta("name", "description", description); setMeta("property", "og:description", description); setMeta("name", "twitter:description", description); }
        setMeta("property", "og:title", full);
        setMeta("name", "twitter:title", full);
        setMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");
        const url = `${PUBLIC_URL}${path}`;
        setMeta("property", "og:url", url);
        let canon = document.head.querySelector('link[rel="canonical"]');
        if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
        canon.href = url;
        document.head.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
        (Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []).forEach((obj) => {
            const s = document.createElement("script");
            s.type = "application/ld+json"; s.dataset.seoJsonld = "1"; s.text = JSON.stringify(obj);
            document.head.appendChild(s);
        });
    }, [title, description, path, noindex, JSON.stringify(jsonLd)]); // eslint-disable-line react-hooks/exhaustive-deps
}

export const FAQ = [
    { q: "Is it safe to upload my bank statement?", a: "Yes. Sift never asks for your bank login. You upload a CSV or PDF export, we read only dates, descriptions and amounts, and everything is stored encrypted against your account. You can delete all of your data in one click from the Account page." },
    { q: "Which UK banks work with Sift?", a: "Any bank that lets you export a statement: Monzo, Starling, Barclays, HSBC, Lloyds, NatWest, Nationwide, Santander, Halifax, Revolut, Chase and more. CSV works best; PDF statements are read by AI." },
    { q: "How much does Sift cost?", a: "The core \"where did my money go\" analysis, transactions, safe-to-spend and 5 Ask Sift questions a day are free. Sift Pro is £4.99 a month and unlocks the Savings Finder, subscription tracker, month-on-month comparison, weekly digest email and unlimited chat." },
    { q: "Can I cancel Sift Pro any time?", a: "Yes. Cancel from your Account page in two clicks. You keep Pro until the end of the period you've paid for and won't be charged again." },
    { q: "How does Sift find savings?", a: "It spots recurring payments (subscriptions, insurance, mobile, broadband, energy, finance), compares each spending category to your usual months, and turns the biggest gaps into ranked actions with a £/month estimate and a one-tap link to a comparison site." },
    { q: "Is Sift financial advice?", a: "No. Sift explains your own spending and suggests practical savings, but it isn't regulated financial advice. For investments, pensions or debt problems speak to a qualified adviser or a free service like StepChange or MoneyHelper." },
];

export const faqJsonLd = () => ({
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: FAQ.map(({ q, a }) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
});

export const appJsonLd = () => ({
    "@context": "https://schema.org", "@type": "SoftwareApplication", name: "Sift", applicationCategory: "FinanceApplication",
    operatingSystem: "Web", url: PUBLIC_URL, image: `${PUBLIC_URL}/og-image.png`,
    description: "Upload a UK bank statement and Sift explains where your money went in plain English, finds subscriptions and shows real savings.",
    offers: [{ "@type": "Offer", price: "0", priceCurrency: "GBP", name: "Free" }, { "@type": "Offer", price: "4.99", priceCurrency: "GBP", name: "Sift Pro", billingIncrement: "P1M" }],
});
